import { inject, Injectable, signal, Type } from '@angular/core';
import { Apollo } from 'apollo-angular';
import { Task } from '../../../shared/entities/task/task';
import { ControlTemplate } from '../../form/entities/controlTemplate';
import { TaskFormHandler } from './task-form-handler.interface';
import { ReviewFormViewComponent } from './components/review-form-view.component';
import { ResourceAssignment } from '../../../shared/entities/resourceAssignment/resourceAssignment';
import * as fromGraphQl from './graphql/rental-resource-assignment-review.graphql';
import { take } from 'rxjs';

/**
 * Handler for rentalResourceAssignmentReviewForm
 * Manages rental resource assignment review forms
 */
@Injectable()
export class RentalResourceAssignmentReviewFormHandler implements TaskFormHandler {
  readonly formKey = 'rentalResourceAssignmentReviewForm';

  // Injected services
  private readonly apollo = inject(Apollo);
  
  // State management with signals
  readonly resourceAssignments = signal<ResourceAssignment[]>([]);
  readonly isLoading = signal(false);

  canHandle(formKey: string): boolean {
    return formKey === this.formKey;
  }

  getComponent(): Type<any> | null {
    return ReviewFormViewComponent;
  }

  initialize(task: Task, template: ControlTemplate | null): void {
    
    // Load resource assignments from task context
    if (task?.context) {
      this.isLoading.set(true);
      
      this.apollo.query<fromGraphQl.RentalResourceAssignmentReviewQuery>({
        query: fromGraphQl.RENTAL_RESOURCE_ASSIGNMENT_REVIEW_QUERY,
        variables: {
          ids: task.context
            .filter(element => element.__type === 'ResourceAssignment')
            .map(element => element.id)
        },
        fetchPolicy: 'network-only',
      })
      .pipe(take(1))
      .subscribe({
        next: (result) => {
          console.log('[RentalResourceAssignmentReviewFormHandler] Resource Assignments loaded:', result);
          const assignments = ResourceAssignment.createResourceAssignments(result?.data?.resourceAssignments);
          this.resourceAssignments.set(assignments);
          this.isLoading.set(false);
        },
        error: (error) => {
          console.error('[RentalResourceAssignmentReviewFormHandler] Error loading resource assignments:', error);
          this.isLoading.set(false);
        }
      });
    }
  }
  
  getState(): any {
    return {
      resourceAssignments: this.resourceAssignments(),
      isLoading: this.isLoading()
    };
  }

  destroy(): void {
    // Cleanup: reset signals
    this.resourceAssignments.set([]);
    this.isLoading.set(false);
  }
}
